import { type ReactNode, forwardRef, useMemo, useState } from 'react';
import {
  ChevronsDownUp,
  ChevronsRight,
  Copy,
  CopyX,
  FolderRoot,
  ListTree,
  type LucideIcon,
  RectangleEllipsis,
  Route,
  Tags,
  Trash2,
  Users,
  Workflow,
} from 'lucide-react';
import { useDndMonitor, useDroppable } from '@dnd-kit/core';
import { useAuth0 } from '@auth0/auth0-react';
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@/components/ui/context-menu';
import { MenuProvider, contextPrimitives } from '@/components/menu-renderer';
import { NewFolderMenuItem } from '@/components/menu-items/node-menu-items';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useFeatureStore } from '@/hooks/data/feature/useFeatureStore';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { useExportStore } from '@/hooks/data/export/useExportStore';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { useSearchStore } from '@/hooks/data/search/useSearchStore';
import { ManageType, useCurrentPage } from '@/hooks/useCurrentPage';
import { TreeSearch } from '@/components/sidebar/tree/tree-search';
import { useBatchStore } from '@/hooks/data/batch/useBatchStore';
import { useTreeSearch } from '@/hooks/data/tree/useTreeSearch';
import { ExportTaskBars } from '@/components/export-task-bars';
import { UploadTaskBars } from '@/components/upload-task-bars';
import { SidebarNav } from '@/components/sidebar/sidebar-nav';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { BatchTaskBars } from '@/components/batch-task-bars';
import { useAccount } from '@/hooks/data/account/useAccount';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { TenantUserRole } from '@/types/tenantUserRole';
import { useTreeContext } from '@/context/TreeContext';
import { Tree } from '@/components/sidebar/tree/tree';
import { Accordion } from '@/components/ui/accordion';
import { useTree } from '@/hooks/data/tree/useTree';
import { DraggableItems } from '@/types/dragDrop';
import { Button } from '@/components/ui/button';
import { SortOrderValues } from '@/types/sort';
import { Badge } from '@/components/ui/badge';
import { EntityType } from '@/types/entity';
import { TaskState } from '@/types/uploads';
import { DAMNodeType } from '@/types/tree';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

export const styles = 'text-text-gray! duration-150 select-none';
export const hoverStyles = 'hover:bg-ui-light!';
// Radix ContextMenuTrigger gets data-state="open" and DropdownMenuTrigger gets
// aria-expanded="true" while the menu is open. Match either to keep the grey
// background painted so the user knows which item the menu applies to.
export const contextMenuOpenStyles =
  'has-[[data-state=open],[aria-haspopup=menu][aria-expanded=true]]:bg-ui-light! data-[state=open]:bg-ui-light!';
export const dropStyle = 'ring-2 ring-highlight-base z-10 rounded-md';
export const selectedStyles =
  'bg-highlight-base text-text-inverted! hover:bg-highlight-medium! dark:focus-visible:bg-highlight-hover-color focus-visible:bg-highlight-hover-color';
export const treeItemIconStyles = 'duration-250 h-6 w-6 min-h-6 min-w-6';
export const selectedTreeItemIconStyles = 'stroke-text-inverted group-hover/tree-item:stroke-text-inverted';

const SidebarButton = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    dataCy: string;
    icon?: LucideIcon;
    iconProps?: any;
    isActive?: boolean;
    className?: string;
    badge?: ReactNode;
    badgeClasses?: string;
    isOver?: boolean;
    onClick?: () => void;
  }
>(function SidebarButton(props, ref) {
  const { label, onClick, icon, iconProps, isActive, className, badge, badgeClasses, isOver, dataCy } = props;

  const Icon = icon;

  return (
    <Button
      variant="ghost"
      data-cy={dataCy}
      size="sm"
      ref={ref}
      onClick={onClick}
      className={cn(
        'group/sidebar-button w-full justify-between pr-3 text-base',
        styles,
        !isOver && hoverStyles,
        isActive && selectedStyles,
        className,
      )}
      {...(Icon && {
        icon: Icon,
        iconClassName: cn('', iconProps.className, {
          'stroke-text-inverted': isActive,
          'stroke-status-failure!': isOver && label === 'Trash',
        }),
      })}
    >
      <div
        className={cn(
          'flex w-full gap-x-2 outline-hidden',
          isOver && label === 'Trash' && `${dropStyle} py-1`,
          badge && 'mr-1',
        )}
      >
        <span className="line-clamp-1 text-left break-all">{label}</span>
      </div>
      {badge && (
        <Badge variant="base" size="sm" className={cn('text-sm px-1.5 h-6 gap-x-0.5', badgeClasses)}>
          {badge}
        </Badge>
      )}
    </Button>
  );
});

export type SidebarButtonProps = {
  label: string;
  dataCy: string;
  icon?: LucideIcon;
  iconProps?: any;
  isActive?: boolean;
  className?: string;
  badge?: ReactNode;
  badgeClasses?: string;
  onClick?: () => void;
};

export const Sidebar = ({ className, rowCount }: { className?: string; rowCount?: number }) => {
  const tCommon = useTranslations('common');
  const tSearch = useTranslations('search');
  const tTree = useTranslations('tree');
  const [openUploadId, setOpenUploadId] = useState<string>('');
  const selectedNode = useTreeStore((state) => state.selectedNode);
  const openModal = useOpenModal();
  const search = useIsSearching();
  const setFilterOnlyFacets = useSearchStore((state) => state.setFilterOnlyFacets);

  const { isTrashPage, isManagePage, isDuplicatesPage, isSharesPage, onTrashSearch } = useCurrentPage();
  const { includeSubFolderAssets, sidebarTreeOpenIds, setSidebarTreeOpenIds } = useTreeContext();
  const { query, push, replace } = useRouter();
  const { deselectAllItems, selectedAssets } = useGallerySelection();
  const { toggleSubFolders, tree, setCurrentSelectedType } = useTree();
  // Boolean selectors: the uploads/batches/exports collections all change identity
  // on every progress write — subscribing to them here would re-render the whole
  // sidebar (Tree, TreeSearch, SidebarNav are not memoized) once per uploaded file
  // chunk, once per second per running batch, and once per export WS event. These
  // selectors return primitives, so the sidebar only re-renders when the Accordion
  // needs to appear or disappear. Per-bar updates stay inside
  // UploadTaskBars/BatchTaskBars/ExportTaskBars.
  const hasBatches = useBatchStore((state) => state.batches.length > 0);
  const hasActiveUploads = useUploadStore((state) =>
    Object.values(state.uploads).some((upload) => upload.uploadState !== TaskState.NotStarted),
  );
  const hasExports = useExportStore((state) => state.exports.length > 0);
  const {
    profile: {
      user: { role: userRole },
    },
  } = useAccount();
  const { isAuthenticated } = useAuth0();
  const { navigateAndReset } = useDamNavigation();
  // Selectors: this wraps the whole tree, so a bare subscription re-renders every row below it.
  const getTableSettings = useTableStore((state) => state.getTableSettings);
  const setPagination = useTableStore((state) => state.setPagination);
  const setLocateAssetId = useBrowserStore((state) => state.setLocateAssetId);
  const {
    searchTerm: treeSearchInput,
    setSearchTerm: setTreeSearchInput,
    committedTerm: treeSearchQuery,
    results: treeSearchResults,
    isFetching: isTreeSearchLoading,
    isActive: isTreeSearchActive,
    clearSearch: clearTreeSearch,
  } = useTreeSearch();

  const assetManagementMenuItems: Array<SidebarButtonProps> = useMemo(
    () => [
      {
        label: 'Shared',
        dataCy: 'sidebar-shares-button',
        icon: Users,
        iconProps: { className: 'h-6 w-6 min-w-6 min-h-6 stroke-feature-sharing', strokeWidth: 2 },
        isActive: isSharesPage,
        // badge: (
        //   <>
        //     37 <AlertTriangle className="icon-4" />
        //   </>
        // ),
        onClick: (): void => {
          void navigateAndReset(
            '/shared',
            {
              pageIndex: 1,
              pageSize: getTableSettings(EntityType.Share).pageSize,
              sortValue: getTableSettings(EntityType.Share).sortValue,
              sortOrder: getTableSettings(EntityType.Share).sortOrder,
            },
            EntityType.Share,
          );
        },
      },
      {
        label: 'Duplicates',
        dataCy: 'sidebar-duplicates-button',
        icon: CopyX,
        iconProps: { className: 'h-6 w-6 min-w-6 min-h-6 stroke-icon-base', strokeWidth: 2 },
        isActive: isDuplicatesPage,
        // badge: <Loader className="icon-4" />,
        onClick: (): void => {
          void navigateAndReset(
            '/duplicates',
            {
              pageIndex: 1,
              pageSize: getTableSettings(EntityType.Duplicate).pageSize,
            },
            EntityType.Duplicate,
          );
        },
      },
      {
        label: 'Trash',
        dataCy: 'sidebar-trash-button',
        icon: Trash2,
        iconProps: { className: 'w-6 h-6 min-h-6 min-w-6 stroke-status-failure', strokeWidth: 2 },
        isActive: isTrashPage || onTrashSearch,
        onClick: (): void => {
          if (search) {
            const newQuery = {
              ...query,
              searchFilters: '',
              filters: 'trashed:true',
            };
            setFilterOnlyFacets([]);
            setLocateAssetId(undefined);
            setPagination(EntityType.Asset, { pageIndex: 0, sortValue: SortOrderValues.CREATED_AT });

            void replace(
              {
                pathname: '/browse',
                query: newQuery,
              },
              undefined,
              { shallow: true },
            );
          } else {
            void navigateAndReset(
              '/trash',
              {
                pageIndex: 1,
                pageSize: getTableSettings(EntityType.Trash).pageSize,
                sortValue: getTableSettings(EntityType.Trash).sortValue,
                sortOrder: getTableSettings(EntityType.Trash).sortOrder,
              },
              EntityType.Trash,
            );
          }
        },
      },
    ],
    [isDuplicatesPage, isTrashPage, deselectAllItems, setCurrentSelectedType, push, query],
  );

  // const recentActivityMenuItems = useMemo(
  //   () => [
  //     {
  //       label: 'Last import',
  //       icon: FolderSearch2,
  //       iconProps: { className: 'h-6 w-6 min-h-6 min-w-6 stroke-smart-folder-color', strokeWidth: 2 },
  //       isActive: false,
  //       disabled: true,
  //     },
  //     {
  //       label: 'Recently Added',
  //       icon: FolderSearch2,
  //       iconProps: { className: 'h-6 w-6 min-h-6 min-w-6 stroke-smart-folder-color', strokeWidth: 2 },
  //       isActive: false,
  //       disabled: true,
  //     },
  //     {
  //       label: 'Recently Updated',
  //       icon: FolderSearch2,
  //       iconProps: { className: 'h-6 w-6 min-h-6 min-w-6 stroke-smart-folder-color', strokeWidth: 2 },
  //       isActive: false,
  //       disabled: true,
  //     },
  //   ],
  //   [],
  // );

  const { setNodeRef, isOver } = useDroppable({
    id: 'trash',
    data: {
      type: 'trash',
      id: 'trash',
      accepts: [DraggableItems.ASSET],
    },
  });

  useDndMonitor({
    onDragEnd(event) {
      void (() => {
        const { active, over } = event;

        if (
          over?.id === 'trash' &&
          over.data.current?.accepts?.includes(active.data.current?.type) &&
          over?.data?.current?.type === 'trash' &&
          active?.data?.current?.type === DraggableItems.ASSET
        ) {
          return openModal(
            ModalId.DeleteConfirmation,
            selectedAssets.length === 1
              ? {
                  id: active.id,
                  name: active.data.current.asset.name,
                  data: {
                    type: active.data.current.type,
                  },
                }
              : { count: rowCount },
          );
        }
      })();
    },
  });

  const configurationLinks = [
    {
      id: ManageType.WORKFLOWS,
      title: 'Workflows',
      icon: Workflow,
      path: '/manage/workflows',
      disabled: false,
    },
    {
      id: ManageType.TAGS,
      title: 'Tags',
      icon: Tags,
      path: '/manage/tags',
      disabled: false,
    },
    {
      id: ManageType.METADATA,
      title: 'Metadata',
      icon: RectangleEllipsis,
      path: '/manage/metadata',
      disabled: false,
    },
    {
      id: ManageType.VARIANTS,
      title: 'Variants',
      icon: Copy,
      path: '/manage/variants',
      disabled: false,
    },
    {
      id: ManageType.DESTINATIONS,
      title: 'Destinations',
      icon: Route,
      path: '/manage/destinations',
      disabled: false,
    },
  ];

  const features = useFeatureStore();
  const linksToShow = configurationLinks.filter((link) => {
    // if there is a flag with this name, only show when truthy
    if (link.id in features) {
      return features[link.id as keyof typeof features];
    }
    // otherwise if there's no flag always show
    return true;
  });

  return isManagePage ? (
    <div className="relative flex h-full flex-col space-y-px overflow-y-auto">
      <SidebarNav
        section={{
          title: tCommon('configuration'),
          icon: ChevronsRight,
        }}
        links={linksToShow}
      />
      {isAuthenticated && userRole === TenantUserRole.Owner && (
        <SidebarNav
          section={{
            title: tCommon('management'),
            icon: ChevronsRight,
          }}
          links={[
            {
              id: 'users',
              title: tCommon('users'),
              icon: Users,
              path: '/manage/users',
              disabled: false,
            },
          ]}
        />
      )}
    </div>
  ) : (
    <div className={cn('flex h-full flex-col space-y-px @container/sidebar', className)}>
      <div className="flex h-12 max-h-12 min-h-12 w-full items-center justify-between gap-x-4 border-t border-t-toolbar-bevel bg-ui-base px-2 py-0">
        <TreeSearch
          value={treeSearchInput}
          onValueChange={setTreeSearchInput}
          onClear={clearTreeSearch}
          isLoading={isTreeSearchLoading}
        />
        <ToggleGroup type="multiple" variant="default" size="sm" className="flex gap-0">
          <ToggleGroupItem disabled={selectedNode?.type === DAMNodeType.Album} value="includeSubfolderAssets" asChild>
            <Button
              variant="main-toolbar"
              size="sm"
              tooltip={includeSubFolderAssets ? tSearch('showOnlySelectedFolder') : tSearch('showAllSubfolders')}
              data-cy="sidebar-include-subfolder-assets-button"
              icon={ListTree}
              iconClassName={cn('icon-6', {
                'stroke-highlight-base!': includeSubFolderAssets,
                'stroke-icon-base!': !includeSubFolderAssets,
              })}
              onClick={() => {
                setLocateAssetId(undefined);
                toggleSubFolders();
              }}
            />
          </ToggleGroupItem>
          <ToggleGroupItem value="collapse" className="hidden @[12.5rem]/sidebar:flex" asChild>
            <Button
              disabled={isTreeSearchActive || isTreeSearchLoading || Object.keys(sidebarTreeOpenIds).length === 0}
              variant="main-toolbar"
              size="sm"
              icon={ChevronsDownUp}
              iconClassName="icon-6"
              tooltip={tCommon('collapse')}
              aria-label={tTree('toggleCollapse')}
              data-cy="sidebar-collapse-tree-button"
              onClick={() => {
                setLocateAssetId(undefined);
                setSidebarTreeOpenIds({});
              }}
            />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div className="flex grow flex-col items-start overflow-y-auto">
        <div className="relative flex w-full grow flex-col items-start overflow-x-hidden overflow-y-auto bg-ui-base">
          {/* <div className="flex flex-col space-y-1 pb-2 pt-1">
            {recentActivityMenuItems.map((item) => (
              <SidebarButton key={item.label} {...item} />
            ))}
          </div> */}
          <Tree
            className="pb-4"
            openIds={sidebarTreeOpenIds}
            tree={tree}
            setOpenIds={setSidebarTreeOpenIds}
            searchResults={treeSearchResults}
            isSearching={isTreeSearchActive}
            isSearchLoading={isTreeSearchLoading}
            searchQuery={treeSearchQuery}
            disableDrag={isTreeSearchActive}
          />
          <ContextMenu>
            <ContextMenuTrigger className="flex w-full grow items-start" />
            <ContextMenuContent>
              <MenuProvider primitives={contextPrimitives}>
                <NewFolderMenuItem icon={FolderRoot} />
              </MenuProvider>
            </ContextMenuContent>
          </ContextMenu>
        </div>
        {(hasActiveUploads || hasBatches || hasExports) && (
          <Accordion
            type="single"
            collapsible
            value={openUploadId}
            onValueChange={(value) => setOpenUploadId(value)}
            className="relative flex max-h-[50%] w-full shrink-0 flex-col space-y-px overflow-x-hidden overflow-y-auto"
          >
            <ExportTaskBars />
            <UploadTaskBars openUploadId={openUploadId} />
            <BatchTaskBars />
          </Accordion>
        )}
      </div>

      <div className="flex flex-col gap-y-1.5 bg-ui-base py-3">
        {assetManagementMenuItems.map((item) => (
          <SidebarButton
            key={item.label}
            {...item}
            {...(item.label === 'Trash' && { ref: setNodeRef })}
            isOver={isOver}
          />
        ))}
      </div>
    </div>
  );
};
